import { useCallback, useEffect, useRef, useState } from 'react';
import { castStrategy } from './castStrategy.js';
import { useCast, type CastControls, type CastStatus } from './useCast.js';

/*
 * Native Google Cast, sender half. The Cast Web Sender framework is loaded
 * from gstatic (secure-context only), pointed at the registered receiver via
 * its App ID, and on session start the sender hands the receiver one message:
 * the TV URL to present (`/host?code=&display=` or `/tv?casino=`). The
 * receiver half lives in `useCastReceiver`; only the slice of the CAF API we
 * touch is declared here — it is not in lib.dom.
 */
export const CAST_NAMESPACE = 'urn:x-cast:com.tvcasino.present';

/** Official CAF loader — this script is what calls `__onGCastApiAvailable`. */
export const SENDER_SDK =
  'https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1';
const SENDER_SDK_ID = 'cast-sender-sdk';

/**
 * The URL the next session (or a resumed one) should hand the receiver. Shared
 * across every sender hook on the page so six console rows do not each send
 * their own table URL when CAF fires SESSION_STARTED once.
 */
let presentUrl: string | null = null;

/** CAF sends plain strings for both enums; only the members we branch on. */
const CAST_STATE_CHANGED = 'caststatechanged';
const SESSION_STATE_CHANGED = 'sessionstatechanged';
const NO_DEVICES = 'NO_DEVICES_AVAILABLE';
/** `chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED` — rejoin our own origin's session. */
const AUTO_JOIN_ORIGIN_SCOPED = 'origin_scoped';

interface CastSessionLike {
  sendMessage(namespace: string, message: unknown): Promise<void>;
}

interface CastContextLike {
  setOptions(options: Record<string, unknown>): void;
  addEventListener(type: string, handler: (event: { sessionState?: string; castState?: string }) => void): void;
  getCastState(): string;
  requestSession(): Promise<void>;
  getCurrentSession(): CastSessionLike | null;
  endCurrentSession(stopCasting: boolean): void;
}

type GlobalWithCast = Window & {
  cast?: {
    framework?: {
      CastContext?: { getInstance(): CastContextLike };
    };
  };
  __onGCastApiAvailable?: (available: boolean, error?: unknown) => void;
};

/** The loaded context, or null before (or without) the framework. */
const castContext = (): CastContextLike | null =>
  (window as GlobalWithCast).cast?.framework?.CastContext?.getInstance() ?? null;

let loadPromise: Promise<CastContextLike | null> | null = null;

/**
 * Injects the sender framework once per page and resolves with the context
 * when it announces itself (`__onGCastApiAvailable`). jsdom never runs the
 * script, so tests drive the callback by hand; a failed load resolves null
 * and the caller falls back to the Presentation API.
 */
function loadSenderSdk(): Promise<CastContextLike | null> {
  if (loadPromise) return loadPromise;
  loadPromise = new Promise((resolve) => {
    const w = window as GlobalWithCast;
    let settled = false;
    const done = (ctx: CastContextLike | null) => {
      if (!settled) {
        settled = true;
        resolve(ctx);
      }
    };
    const timeout = window.setTimeout(() => done(castContext()), 8000);
    const finish = (ctx: CastContextLike | null) => {
      window.clearTimeout(timeout);
      done(ctx);
    };
    w.__onGCastApiAvailable = (available) => finish(available ? castContext() : null);
    if (document.getElementById(SENDER_SDK_ID)) return; // already loading; the callback decides
    const script = document.createElement('script');
    script.id = SENDER_SDK_ID;
    script.src = SENDER_SDK;
    script.async = true;
    script.onerror = () => finish(null);
    document.head.appendChild(script);
  });
  return loadPromise;
}

/**
 * The `google` half of the cast button. Returns null until the framework is
 * ready — and null forever when there is no App ID, no secure context or a
 * failed load — so the caller can fall back to `useCast` while we keep quiet.
 * The returned controls speak the same `CastControls` shape as `useCast`,
 * which is what lets one `CastButton` drive either route.
 */
export function useGoogleCast(url: string | null, appId: string | null): CastControls | null {
  // The secure-context sniff is stable for the life of the page.
  const [secure] = useState(() => typeof window !== 'undefined' && window.isSecureContext);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [status, setStatus] = useState<CastStatus>('idle');
  const [available, setAvailable] = useState(false);
  const [checked, setChecked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const urlRef = useRef(url);
  urlRef.current = url;

  useEffect(() => {
    if (!appId || !secure) return;
    let cancelled = false;
    void loadSenderSdk().then((ctx) => {
      if (cancelled) return;
      if (!ctx) {
        setFailed(true);
        return;
      }
      ctx.setOptions({
        receiverApplicationId: appId,
        // The CAF enum members are lowercase strings, not their own names:
        // `chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED === 'origin_scoped'`. An
        // unknown value here is ignored, which silently drops auto-rejoin of
        // a session this origin already started.
        autoJoinPolicy: AUTO_JOIN_ORIGIN_SCOPED,
      });
      // Availability arrives with the event payload, synchronously after
      // setOptions, and on every change; `checked` flips on the first read so
      // the button never guesses about devices it has not heard from.
      const readCastState = (event?: { castState?: string }) => {
        setChecked(true);
        setAvailable((event?.castState ?? ctx.getCastState()) !== NO_DEVICES);
      };
      ctx.addEventListener(CAST_STATE_CHANGED, readCastState);
      ctx.addEventListener(SESSION_STATE_CHANGED, (event) => {
        if (event.sessionState === 'SESSION_STARTED' || event.sessionState === 'SESSION_RESUMED') {
          // Re-hand the URL on every start: a resumed session belongs to a
          // receiver that may have just rebooted and lost the last one.
          void present(presentUrl ?? urlRef.current, ctx);
          setStatus('casting');
          setError(null);
        } else if (event.sessionState === 'SESSION_ENDED') {
          setStatus('idle');
        }
      });
      readCastState();
      setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [appId]);

  const start = useCallback(() => {
    const ctx = castContext();
    if (!ctx || !ready) return;
    presentUrl = urlRef.current;
    setError(null);
    setStatus('connecting');
    ctx
      .requestSession()
      .then(() => {
        const session = ctx.getCurrentSession();
        if (!session) {
          setStatus('idle');
          return;
        }
        // requestSession resolves once the session has started, so hand the
        // URL straight over; the SESSION_RESUMED listener covers re-attaches.
        void present(presentUrl, ctx);
        setStatus('casting');
      })
      .catch((err: unknown) => {
        const name = err instanceof Error ? err.name : '';
        setStatus('idle');
        // Dismissing the device picker is not an error worth showing.
        if (name === 'AbortError' || name === 'NotAllowedError') return;
        setError('Could not start casting.');
      });
  }, [ready]);

  const stop = useCallback(() => {
    setStatus('idle');
    castContext()?.endCurrentSession(true);
  }, []);

  // Placeholder while CAF loads: Android's Presentation API path is a dead
  // end, and showing that while the sender SDK is still arriving looks like
  // "there is no Cast button." Failed loads fall through to presentation.
  const configured = typeof appId === 'string' && appId.trim().length > 0;
  if (configured && secure && !ready && !failed) {
    return {
      status: 'idle',
      available: false,
      checked: false,
      block: null,
      error: null,
      start,
      stop,
    };
  }
  if (castStrategy({ appId, secure, senderSdk: ready }) !== 'google') return null;
  return { status, available, checked, block: null, error, start, stop };
}

/**
 * Native Google Cast when the App ID, HTTPS and the sender SDK are all
 * present; Presentation API everywhere else. Host and every console table
 * row share this so CAST_APP_ID actually reaches the button you tap.
 */
export function useSenderCast(url: string | null, appId: string | null): CastControls {
  const google = useGoogleCast(url, appId);
  const presentation = useCast(google ? null : url);
  return google ?? presentation;
}

/** Hands the TV URL to the receiver over the custom namespace. */
async function present(url: string | null, ctx: CastContextLike): Promise<void> {
  if (!url) return;
  try {
    await ctx.getCurrentSession()?.sendMessage(CAST_NAMESPACE, { url });
  } catch {
    // The receiver retries nothing; the sender's next state change re-sends.
  }
}
